import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Inbox as InboxIcon,
  QrCode as QrCodeIcon,
  ArrowForward as ArrowForwardIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  UnfoldMore as UnfoldMoreIcon
} from '@mui/icons-material';
import { MarkingCodeItem, SortField, Order } from '../types';
import { STATUS_COLORS } from '../constants';

interface MarkingCodesTableProps {
  items: MarkingCodeItem[];
  totalCount: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  orderBy: SortField;
  order: Order;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onSort: (field: SortField) => void;
  onDocumentClick?: (documentId: number) => void;
}

// Заголовочная ячейка таблицы с поддержкой сортировки как в OnlineJournal
const HeaderCell: React.FC<{
  children: React.ReactNode;
  sortField?: SortField;
  orderBy: SortField;
  order: Order;
  onSort?: (field: SortField) => void;
}> = ({ children, sortField, orderBy, order, onSort }) => {
  const isActive = sortField && orderBy === sortField;
  return (
    <Box
      onClick={sortField && onSort ? () => onSort(sortField) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: sortField && onSort ? 'pointer' : 'default',
        userSelect: 'none',
        '&:hover': sortField && onSort ? { color: 'primary.main' } : {}
      }}
    >
      <Typography
        variant="caption"
        fontWeight={isActive ? 700 : 600}
        color={isActive ? 'primary.main' : 'text.secondary'}
        sx={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}
      >
        {children}
      </Typography>
      {sortField && onSort && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isActive ? (
            order === 'asc' ? (
              <ArrowUpIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            ) : (
              <ArrowDownIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            )
          ) : (
            <UnfoldMoreIcon sx={{ fontSize: 14, color: 'text.disabled', opacity: 0.6 }} />
          )}
        </Box>
      )}
    </Box>
  );
};

export const MarkingCodesTable: React.FC<MarkingCodesTableProps> = ({
  items,
  totalCount,
  isLoading,
  page,
  pageSize,
  orderBy,
  order,
  onPageChange,
  onPageSizeChange,
  onSort,
  onDocumentClick
}) => {
  const theme = useTheme();
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusChip = (item: MarkingCodeItem) => {
    const statusLabel = item.status || 'Не указан';
    const colorKey = (item.statusZid !== undefined && STATUS_COLORS[item.statusZid])
      ? STATUS_COLORS[item.statusZid]
      : STATUS_COLORS[item.status] || 'default';

    let colorHex = theme.palette.text.secondary;
    if (colorKey === 'success') colorHex = theme.palette.success.main;
    else if (colorKey === 'info') colorHex = theme.palette.info.main;
    else if (colorKey === 'warning') colorHex = theme.palette.warning.main;
    else if (colorKey === 'primary') colorHex = theme.palette.primary.main;
    else if (colorKey === 'error') colorHex = theme.palette.error.main;

    return (
      <Chip
        label={statusLabel}
        size="small"
        sx={{
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: alpha(colorHex, 0.1),
          color: colorHex,
          border: '1px solid',
          borderColor: alpha(colorHex, 0.25)
        }}
      />
    );
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <TableContainer sx={{ minHeight: 420 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Код маркировки */}
              <TableCell sx={{ py: 1.5, width: '22%' }}>
                <HeaderCell sortField="code" orderBy={orderBy} order={order} onSort={onSort}>
                  Код маркировки
                </HeaderCell>
              </TableCell>

              {/* Товар */}
              <TableCell sx={{ py: 1.5, width: '24%' }}>
                <HeaderCell sortField="materialname" orderBy={orderBy} order={order} onSort={onSort}>
                  Товар
                </HeaderCell>
              </TableCell>

              {/* Тип документа */}
              <TableCell sx={{ py: 1.5, width: '13%' }}>
                <HeaderCell sortField="type" orderBy={orderBy} order={order} onSort={onSort}>
                  Тип
                </HeaderCell>
              </TableCell>

              {/* Статус */}
              <TableCell sx={{ py: 1.5, width: '13%' }}>
                <HeaderCell sortField="status" orderBy={orderBy} order={order} onSort={onSort}>
                  Статус
                </HeaderCell>
              </TableCell>

              {/* Номер документа */}
              <TableCell sx={{ py: 1.5, width: '9%' }}>
                <HeaderCell sortField="documentNumber" orderBy={orderBy} order={order} onSort={onSort}>
                  № док.
                </HeaderCell>
              </TableCell>

              {/* Маршрут (Отправитель → Получатель) */}
              <TableCell sx={{ py: 1.5, width: '11%' }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Маршрут
                </Typography>
              </TableCell>

              {/* Дата */}
              <TableCell sx={{ py: 1.5, width: '8%' }}>
                <HeaderCell sortField="date" orderBy={orderBy} order={order} onSort={onSort}>
                  Дата
                </HeaderCell>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              Array.from(new Array(pageSize > 10 ? 10 : pageSize)).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={90} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                </TableRow>
              ))
            ) : items.length > 0 ? (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    height: 52,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  {/* Код маркировки */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <QrCodeIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: 'text.primary',
                          wordBreak: 'break-all',
                          fontWeight: 500
                        }}
                      >
                        {item.code}
                      </Typography>
                      <Tooltip title={copiedCode === item.code ? 'Скопировано!' : 'Скопировать код'}>
                        <IconButton size="small" onClick={() => handleCopy(item.code)} sx={{ flexShrink: 0 }}>
                          {copiedCode === item.code ? (
                            <CheckIcon fontSize="small" color="success" />
                          ) : (
                            <ContentCopyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {/* Товар */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.84rem'
                      }}
                      title={item.materialName}
                    >
                      {item.materialName || '—'}
                    </Typography>
                  </TableCell>

                  {/* Тип документа */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                      {item.type || '—'}
                    </Typography>
                  </TableCell>

                  {/* Статус */}
                  <TableCell>{getStatusChip(item)}</TableCell>

                  {/* Номер документа */}
                  <TableCell>
                    {item.documentNumber ? (
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        fontFamily="monospace"
                        onClick={() => onDocumentClick?.(Number(item.documentNumber))}
                        sx={{
                          color: 'primary.main',
                          cursor: onDocumentClick ? 'pointer' : 'default',
                          display: 'inline-block',
                          borderRadius: 1,
                          px: 0.6,
                          py: 0.2,
                          transition: 'all 0.15s ease',
                          '&:hover': onDocumentClick ? {
                            bgcolor: 'action.hover',
                            textDecoration: 'underline'
                          } : undefined
                        }}
                      >
                        #{item.documentNumber}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>

                  {/* Маршрут */}
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography variant="caption" color="text.secondary" noWrap title={item.stockNameOut}>
                        {item.stockNameOut || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ArrowForwardIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                        <Typography variant="caption" fontWeight={600} color="text.primary" noWrap title={item.stockNameIn}>
                          {item.stockNameIn || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Дата */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {item.date || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 6,
                      bgcolor: 'background.default'
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: 'text.secondary'
                      }}
                    >
                      <InboxIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Коды маркировки не найдены
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ maxWidth: 400 }}
                    >
                      Попробуйте изменить параметры фильтрации или сбросить фильтры
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
        labelRowsPerPage="Строк на странице:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count !== -1 ? count.toLocaleString('ru-RU') : `более чем ${to}`}`}
        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
      />
    </Paper>
  );
};
